import { MouseEvent } from "react";
import { buildTenantContextHref, TenantContextUrlOptions } from "./buildTenantContextUrl";
import { resolveCurrentTenantId } from "./tenantContext";

export interface AppLinkProps {
    href: string;
    onClick: (event: MouseEvent<HTMLElement>) => void;
}

/**
 * True for the click the browser would handle as "follow this link right here": primary button, no
 * modifier keys, nothing upstream having already claimed it. Everything else — middle click,
 * Cmd/Ctrl-click, Shift/Alt-click — is left to the browser so its native new-tab / new-window
 * handling applies (NG-8661).
 */
export const isPlainLeftClick = (event: MouseEvent<HTMLElement>): boolean =>
    !event.defaultPrevented && event.button === 0 && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey;

/**
 * Props for an in-app navigation target that behaves like a real link (NG-8661).
 *
 * The two halves deliberately differ:
 *  - `href` carries `?ctx=<tenantId>` — a new tab starts with empty sessionStorage, so the tenant
 *    context has to travel on the address or the tab boots into the default organization.
 *  - `onClick` routes to the CLEAN path via react-router. `?ctx=` is only scrubbed on boot, so
 *    handing it to a client-side navigation would strand it in the address bar and expose it to
 *    every `useSearchParams` consumer on the destination page.
 *
 * `to` should be an app-absolute path (e.g. `/lab/patients/42`), optionally with a query string.
 */
export const getAppLinkProps = (to: string, navigate: (path: string) => void, tenantId?: number | null, options?: TenantContextUrlOptions): AppLinkProps => ({
    href: buildTenantContextHref(to, tenantId ?? resolveCurrentTenantId(), options),
    onClick: (event: MouseEvent<HTMLElement>) => {
        if (!isPlainLeftClick(event)) {
            return;
        }
        event.preventDefault();
        navigate(to);
    },
});
